import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnChanges,
  SimpleChanges,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Permissions } from '@guard/Permissions';
import { PermissionsService } from '@services/helpers/permissions.service';
import { PartnerDealQuotedLoanResponse } from '@interfaces/partners/responses/partner-deal-quoted-loan.response';
import { PartnersHttpService } from '@services/http/partners/partners-http.service';
import { SharedModule } from '@src/app/shared/shared.module';
import { VaultFormatCurrencyPipe } from '@src/app/shared/pipes/vault-format-currency.pipe';
import { VaultFormatPercentPipe } from '@src/app/shared/pipes/vault-format-percent.pipe';
import { mensagemDoErro } from '@features/dashboard/meetings/components/meeting-presenter';
import { Subscription, first } from 'rxjs';
import { ModalLoanDetailsComponent } from '@features/dashboard/costs/debt-management/pages/modal-loan-details/modal-loan-details.component';
import { loanOperationTypeLabel } from './deal-quotes-presenter';

/**
 * Aba **Cotação** da tela do deal (card 3611).
 *
 * Os empréstimos com status **Cotado** da empresa do deal, em **todas as etapas**, para o time interno e
 * para o parceiro. A rota é a do deal (`partners/deals/{id}/quoted-loans`): o servidor confere que o deal é
 * visível para quem pede e usa a empresa do próprio deal; a lista não depende das permissões da Gestão de
 * Empréstimos. Abrir a modal do empréstimo, sim: pede `LoanOperation.VIEW`, que o parceiro tem só de leitura.
 *
 * Colunas e posição da aba (última, depois de Linha do tempo) decididas pelo Marcio em 21/10/2026, na
 * página de perguntas do card no acervo: Banco, Modalidade, Valor, Valor (BRL), Taxa, Custo all-in e Data da
 * operação.
 *
 * O **nome do banco** é o link para quem tem `LoanOperation.VIEW` (sem a permissão, texto; sem banco, "Ver
 * empréstimo"): abre aqui mesmo, na tela do deal, a
 * mesma modal de detalhes da Gestão de Empréstimos (`app-modal-loan-details`). Para o parceiro ela abre em
 * **só leitura** (`readOnlyLoan`): sem menu ⋮, sem chat de IA e sem nenhuma ação que grava ou leva para tela
 * interna. O backend dá ao parceiro só duas permissões de leitura, `LoanOperation.VIEW` e
 * `LoanOperation.ATTACHMENT_LIST` (decisão do Breno, 21/10/2026); os menus da Gestão de Empréstimos, Derivativos, Curvas de mercado e Empresas continuam
 * escondidos para ele.
 *
 * Mesmo desenho do painel de Ligações: estado todo daqui, zerado a cada troca de `dealId`, e falha ao
 * carregar fica **dentro** do painel, com "Tentar de novo", sem derrubar a tela do deal.
 */
@Component({
  selector: 'app-deal-quotes-panel',
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    VaultFormatCurrencyPipe,
    VaultFormatPercentPipe,
    ModalLoanDetailsComponent,
  ],
  templateUrl: './deal-quotes-panel.component.html',
  styleUrl: './deal-quotes-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DealQuotesPanelComponent implements OnChanges {
  readonly dealId = input.required<string>();
  /**
   * Modal do empréstimo em só leitura. Padrão `true` (parceiro); a tela do deal passa `false` para o time
   * interno (`!visaoInterna`), que abre a modal completa.
   */
  readonly readOnlyLoan = input(true);

  /**
   * O banco só vira link para quem tem `LoanOperation.VIEW`, que a modal do empréstimo exige (parceiro e interno
   * com acesso à Gestão de Empréstimos). Sem ela a modal abriria cheia de erros de permissão; o banco fica texto.
   */
  protected readonly canOpenLoan = toSignal(
    inject(PermissionsService).hasPermission(Permissions.LoanOperation.VIEW),
    { initialValue: false }
  );

  protected readonly loans = signal<PartnerDealQuotedLoanResponse[]>([]);
  protected readonly loading = signal(false);
  protected readonly error = signal<string | null>(null);

  protected readonly typeLabel = loanOperationTypeLabel;

  private readonly destroyRef = inject(DestroyRef);
  private readonly partnersService = inject(PartnersHttpService);
  private readonly loanDetailsModal = viewChild(ModalLoanDetailsComponent);
  private request?: Subscription;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dealId']) {
      this.loans.set([]);
      this.load();
    }
  }

  /** Carrega as cotações do deal; também é o "Tentar de novo". */
  load(): void {
    const dealId = this.dealId();
    if (!dealId) return;

    this.request?.unsubscribe();
    this.loading.set(true);
    this.error.set(null);

    this.request = this.partnersService
      .getDealQuotedLoans(dealId)
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: loans => {
          this.loading.set(false);
          this.loans.set(loans);
        },
        error: err => {
          this.loading.set(false);
          this.error.set(
            mensagemDoErro(
              err,
              'Não foi possível carregar as cotações deste deal. Use Tentar de novo; se continuar, avise o time Legatus.'
            )
          );
        },
      });
  }

  /** Abre a modal de detalhes do empréstimo na própria tela do deal (só leitura para o parceiro). */
  protected openLoan(loan: PartnerDealQuotedLoanResponse): void {
    this.loanDetailsModal()?.open(loan.id);
  }
}
